import React, { useContext } from "react";
import { Alert, Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { Megaphone, Plus } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import DashboardPage from "../dashboard/DashboardPage";
import BusinessSettingsContext from "../Context/Settings/BusinessSettingsContext";
import CampaignList from "./CampaignList";
import ListManager from "./ListManager";
import CampaignCreate from "./CampaignCreate";
import CampaignDetails from "./CampaignDetails";
import { campaignSearch, campaignSectionFromSearch, CampaignSection } from "./campaignNavigation";

const sections = [
  { value: "campaigns", label: "All campaigns" },
  { value: "contacts", label: "Lists & contacts" },
  { value: "analytics", label: "Analytics" },
] as const;

export default function CampaignLayout() {
  const { settings } = useContext(BusinessSettingsContext);
  const businessId = settings?.businessId || "";
  const [searchParams, setSearchParams] = useSearchParams();
  const section = campaignSectionFromSearch(searchParams);
  const campaignId = searchParams.get("campaignId") || "";
  const navigateSection = (next: CampaignSection, id = "") =>
    setSearchParams(previous => campaignSearch(previous, next, id));

  return (
    <DashboardPage
      title="Campaigns"
      icon={<Megaphone aria-hidden="true" />}
      description="Manage audiences, send approved messages and follow delivery results."
      actions={businessId ? <Button variant={section === "create" ? "outlined" : "contained"} startIcon={<Plus size={18} aria-hidden="true" />} aria-pressed={section === "create"} onClick={() => navigateSection("create")}>Create campaign</Button> : undefined}
    >
      {!businessId.trim() ? (
        <Alert severity="warning" action={<Button component={Link} to="/settings?tab=0" color="inherit">Business settings</Button>}>
          Complete your business settings to use campaigns.
        </Alert>
      ) : <>
        <Box sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 3, minWidth: 0 }}>
          <Tabs value={section === "create" ? false : section} onChange={(_, value) => navigateSection(value)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label="Campaign sections">
            {sections.map(item => <Tab key={item.value} value={item.value} label={item.label} id={`campaign-tab-${item.value}`} aria-controls={`campaign-panel-${item.value}`} />)}
          </Tabs>
        </Box>
        <Box key={section} role={section === "create" ? "region" : "tabpanel"} id={`campaign-panel-${section}`} aria-labelledby={section === "create" ? "campaign-create-title" : `campaign-tab-${section}`} sx={{ minWidth: 0 }}>
          {section === "campaigns" && <CampaignList businessId={businessId} onSelectCampaign={id => navigateSection("analytics", id)} />}
          {section === "contacts" && <ListManager businessId={businessId} />}
          {section === "create" && <CampaignCreate businessId={businessId} onCreated={id => navigateSection("analytics", id)} />}
          {section === "analytics" && (campaignId ? (
            <CampaignDetails businessId={businessId} campaignId={campaignId} onOpenCampaign={id => navigateSection("analytics", id)} onBack={() => navigateSection("campaigns")} />
          ) : <Box sx={{ px: 3, py: 5, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper", maxWidth: 720 }}>
            <Typography component="h2" variant="h6">Choose a campaign to see its results</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>Open a campaign to review delivery, engagement and follow-up details.</Typography>
            <Button variant="outlined" onClick={() => navigateSection("campaigns")}>Browse campaigns</Button>
          </Box>)}
        </Box>
      </>}
    </DashboardPage>
  );
}
